import { useMemo } from "react";

import { compact, sortBy } from "lodash/fp";

import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import type { MeetOpsQuery } from "@app/analytics/meetops/types";
import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import {
  getDateRanges,
  getQueryKeyFromDateRanges,
  ParsedQueryResultRequiredBy,
  parseMetricsDataFromQueryResult,
  parseMetricsDataListFromQueryResult,
} from "@app/analytics/utils";
import { useAuthenticated, User } from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { CoachingSession, ComputedCoachingData } from "@app/common/types";
import { getSemanticScore, SemanticScore } from "@app/common/utils";

import useCoachingSessions, { SessionMap } from "./useCoachingSessions";

type ApiAggregateSessionData = ParsedQueryResultRequiredBy<
  "participantBias" | "participantCharisma"
>;

type ApiBySessionData = ParsedQueryResultRequiredBy<
  "participantBias" | "participantCharisma" | "meetingId"
>;

type ApiByParticipantData = ParsedQueryResultRequiredBy<
  "participantBias" | "participantCharisma" | "participantName"
>;

const SCORES_TO_INCLUDE: SemanticScore[] = ["negative", "neutral"];

const METRICS: MeetOpsQuery["metrics"] = [
  "participantBias",
  "participantCharisma",
];

const DEFAULT_DATA: ComputedCoachingData = {
  average: null,
  semanticScore: "unknown",
  sessions: [],
};

const DEFAULT_RESULT: ApiAggregateSessionData = {
  participantBias: { value: null, formattedValue: "-" },
  participantCharisma: { value: null, formattedValue: "-" },
  participantName: null,
};

const getAggregatedMetricsQuery = (
  dateRanges: MeetOpsQuery["dateRanges"],
  userId: User["id"]
): MeetOpsQuery => ({
  dateRanges,
  dimensions: [],
  metrics: METRICS,
  filters: [],
  people: "everyone_but_me",
  userId,
});

const getMetricsBySessionQuery = (
  dateRanges: MeetOpsQuery["dateRanges"],
  userId: User["id"]
): MeetOpsQuery => ({
  dateRanges,
  dimensions: ["meetingId"],
  metrics: METRICS,
  filters: [],
  people: "everyone_but_me",
  userId,
});

const getMetricsByParticipant = (
  dateRanges: MeetOpsQuery["dateRanges"],
  userId: User["id"]
): MeetOpsQuery => ({
  dateRanges,
  dimensions: ["participantName"],
  metrics: METRICS,
  filters: [],
  people: "everyone_but_me",
  userId,
});

const getHighestBiasParticipant = (
  byParticipantData: ApiByParticipantData[]
) => {
  const participant = sortBy(
    "participantBias.value",
    byParticipantData.filter(
      (d) => Boolean(d.participantBias.value) && Boolean(d.participantName)
    )
  )[0];

  if (participant) {
    return {
      // defaults to appease the types, neither should be null due to the filter above
      name: participant.participantName ?? "",
      value: participant.participantBias.value ?? 0,
    };
  }
};

const getLowestCharismaParticipant = (
  byParticipantData: ApiByParticipantData[]
) => {
  const participant = sortBy(
    "participantCharisma.value",
    byParticipantData.filter(
      (d) => Boolean(d.participantCharisma.value) && Boolean(d.participantName)
    )
  )[0];

  if (participant) {
    return {
      // defaults to appease the types, neither should be null due to the filter above
      name: participant.participantName ?? "",
      value: participant.participantCharisma.value ?? 0,
    };
  }
};

const addMetricsToSessions = (
  sessions: SessionMap,
  bySessionData: ApiBySessionData[]
): CoachingSession[] => {
  const combinedSessions = bySessionData.map((data) => {
    if (!data.meetingId) return;

    const session = sessions.get(data.meetingId);
    if (!session) return;

    return {
      ...session,
      bias: data.participantBias,
      charisma: data.participantCharisma,
      // irrelevant for bias/charisma sections
      rate: 0,
      count: 0,
      words: [],
    };
  });

  return compact(combinedSessions);
};

const combineData = (
  aggregateData: ApiAggregateSessionData,
  bySessionData: ApiBySessionData[],
  byParticipantData: ApiByParticipantData[],
  coachingSessions: SessionMap
) => {
  const combinedSessions = addMetricsToSessions(
    coachingSessions,
    bySessionData
  );

  const bias: ComputedCoachingData = {
    average: aggregateData.participantBias.value,
    participant: getHighestBiasParticipant(byParticipantData),
    semanticScore: getSemanticScore(aggregateData.participantBias.value),
    sessions: combinedSessions.filter((session) =>
      SCORES_TO_INCLUDE.includes(getSemanticScore(session.bias.value))
    ),
  };

  const charisma: ComputedCoachingData = {
    average: aggregateData.participantCharisma.value,
    participant: getLowestCharismaParticipant(byParticipantData),
    semanticScore: getSemanticScore(aggregateData.participantCharisma.value),
    sessions: combinedSessions.filter((session) =>
      SCORES_TO_INCLUDE.includes(getSemanticScore(session.charisma.value))
    ),
  };

  return { bias, charisma };
};

const useCoachingDashboardCharismaBias = () => {
  const { user } = useAuthenticated();
  const { canAccessCoaching } = useFeatureAccess();

  const dateRanges = useDateRangeStore(getDateRanges);

  const dateRangeQueryKey = getQueryKeyFromDateRanges(dateRanges);

  const queryOptions = {
    enabled: canAccessCoaching,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  };

  const aggregateMetricsQuery = useMetricsQuery(
    getAggregatedMetricsQuery(dateRanges, user.id),
    {
      queryKey: [
        "coaching",
        "metrics",
        "aggregate",
        user.id,
        dateRangeQueryKey,
      ],
      ...queryOptions,
    }
  );

  const bySessionQuery = useMetricsQuery(
    getMetricsBySessionQuery(dateRanges, user.id),
    {
      queryKey: [
        "coaching",
        "metrics",
        "bySession",
        user.id,
        dateRangeQueryKey,
      ],
      ...queryOptions,
    }
  );

  const byParticipantQuery = useMetricsQuery(
    getMetricsByParticipant(dateRanges, user.id),
    {
      queryKey: [
        "coaching",
        "metrics",
        "byParticipant",
        user.id,
        dateRangeQueryKey,
      ],
      ...queryOptions,
    }
  );

  const { sessions, ...sessionsQuery } = useCoachingSessions({
    dateRanges,
    userId: user.id,
    enabled: canAccessCoaching,
  });

  const [aggregateData] =
    parseMetricsDataFromQueryResult<ApiAggregateSessionData>(
      aggregateMetricsQuery.data,
      DEFAULT_RESULT
    );

  const [bySessionData] = parseMetricsDataListFromQueryResult<ApiBySessionData>(
    bySessionQuery.data
  );

  const [byParticipantData] =
    parseMetricsDataListFromQueryResult<ApiByParticipantData>(
      byParticipantQuery.data
    );

  const refetch = () => {
    if (aggregateMetricsQuery.isError) aggregateMetricsQuery.refetch();
    if (bySessionQuery.isError) bySessionQuery.refetch();
    if (byParticipantQuery.isError) byParticipantQuery.refetch();
    if (sessionsQuery.isError) sessionsQuery.refetch();
  };

  const isInitialLoading =
    aggregateMetricsQuery.isInitialLoading ||
    bySessionQuery.isInitialLoading ||
    byParticipantQuery.isInitialLoading ||
    sessionsQuery.isLoading;

  const isError =
    aggregateMetricsQuery.isError ||
    bySessionQuery.isError ||
    byParticipantQuery.isError ||
    sessionsQuery.isError;

  const { bias, charisma } = useMemo(() => {
    if (!aggregateData || !bySessionData || !byParticipantData) {
      return { bias: DEFAULT_DATA, charisma: DEFAULT_DATA };
    }

    return combineData(
      aggregateData,
      bySessionData,
      byParticipantData,
      sessions
    );
  }, [isInitialLoading]);

  const noData =
    !isInitialLoading && bias.average == null && charisma.average == null;

  return {
    bias,
    charisma,
    isError,
    isInitialLoading,
    noData,
    refetch,
  };
};

export default useCoachingDashboardCharismaBias;
